import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiMail, FiSun, FiMoon, FiArrowLeft } from 'react-icons/fi';
import { useTheme } from '../contexts/ThemeContext';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import { authAPI } from '../api/endpoints';
import { validateEmail } from '../utils/helpers';
import { showToast } from '../components/common/Toaster';
import logo from '../assets/images/logo.png';
import {
  LoginContainer,
  LoginCard,
  LoginLeft,
  LoginRight,
  Logo,
  LoginTitle,
  LoginSubtitle,
  LoginForm,
  SignupPrompt,
  ThemeToggle,
  BackgroundShape,
  FeatureList,
  FeatureItem,
  FeatureIcon,
  BackButton,
} from './Login.styles';

const ForgotPassword = () => {
  const { isDarkMode, toggleTheme } = useTheme();
  
  const [formData, setFormData] = useState({
    email: '',
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    setLoading(true);
    
    try {
      await authAPI.passwordResetRequest(formData.email);
      
      setIsSubmitted(true);
      showToast('success', 'Password reset instructions sent to your email!', 5000);
      
      // Redirect to reset password page with email parameter
      setTimeout(() => {
        window.location.href = `/reset-password?email=${encodeURIComponent(formData.email)}`;
      }, 2000);
    } catch (error) {
      setErrors({
        general: error.response?.data?.error || 'Failed to send reset email. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <LoginContainer>
        <BackgroundShape className="shape-1" />
        <BackgroundShape className="shape-2" />
        <BackgroundShape className="shape-3" />
        
        <ThemeToggle onClick={toggleTheme}>
          {isDarkMode ? <FiSun /> : <FiMoon />}
        </ThemeToggle>
        
        <LoginCard>
          <LoginLeft>
            <Logo style={{ justifyContent: 'center' }}>
              <img src={logo} alt="Dira Learners Logo" style={{ width: '200px', height: 'auto' }} />
            </Logo>
            
            <FeatureList>
              <FeatureItem>
                <FeatureIcon>📧</FeatureIcon>
                <div>
                  <h4>Check Your Email</h4>
                  <p>We've sent password reset instructions to your email address</p>
                </div>
              </FeatureItem>
              
              <FeatureItem>
                <FeatureIcon>🔒</FeatureIcon>
                <div>
                  <h4>Secure Reset</h4>
                  <p>Your password reset link is valid for 1 hour</p>
                </div>
              </FeatureItem>
              
              <FeatureItem>
                <FeatureIcon>⚡</FeatureIcon>
                <div>
                  <h4>Quick Process</h4>
                  <p>Reset your password in just a few clicks</p>
                </div>
              </FeatureItem>
            </FeatureList>
          </LoginLeft>
          
          <LoginRight>
            <div style={{ textAlign: 'center' }}>
              <div style={{ 
                width: '80px', 
                height: '80px', 
                background: 'linear-gradient(135deg, #10B981, #059669)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
                fontSize: '32px'
              }}>
                ✓
              </div>
              
              <LoginTitle>Check Your Email</LoginTitle>
              <LoginSubtitle>
                We've sent password reset instructions to <strong>{formData.email}</strong>
              </LoginSubtitle>
              
              <div style={{ 
                background: '#F0FDF4',
                border: '1px solid #BBF7D0',
                borderRadius: '12px',
                padding: '16px',
                margin: '24px 0',
                color: '#166534'
              }}>
                <p style={{ margin: 0, fontSize: '14px' }}>
                  Didn't receive the email? Check your spam folder or try again.
                </p>
              </div>
              
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <Button
                  variant="outline"
                  onClick={() => setIsSubmitted(false)}
                >
                  Try Another Email
                </Button>
                
                <Link to="/login">
                  <Button variant="primary">
                    Back to Login
                  </Button>
                </Link>
              </div>
            </div>
          </LoginRight>
        </LoginCard>
      </LoginContainer>
    );
  }

  return (
    <LoginContainer>
      <BackgroundShape className="shape-1" />
      <BackgroundShape className="shape-2" />
      <BackgroundShape className="shape-3" />
      
      <ThemeToggle onClick={toggleTheme}>
        {isDarkMode ? <FiSun /> : <FiMoon />}
      </ThemeToggle>
      
      <LoginCard>
        <LoginLeft>
          <Logo style={{ justifyContent: 'center' }}>
            <img src={logo} alt="Dira Learners Logo" style={{ width: '200px', height: 'auto' }} />
          </Logo>
          
          <FeatureList>
            <FeatureItem>
              <FeatureIcon>🔐</FeatureIcon>
              <div>
                <h4>Secure Reset</h4>
                <p>Your password reset is completely secure and encrypted</p>
              </div>
            </FeatureItem>
            
            <FeatureItem>
              <FeatureIcon>⚡</FeatureIcon>
              <div>
                <h4>Quick Process</h4>
                <p>Reset your password in just a few simple steps</p>
              </div>
            </FeatureItem>
            
            <FeatureItem>
              <FeatureIcon>📧</FeatureIcon>
              <div>
                <h4>Email Instructions</h4>
                <p>We'll send you detailed instructions via email</p>
              </div>
            </FeatureItem>
          </FeatureList>
        </LoginLeft>
        
        <LoginRight>
          <BackButton to="/login">
            <FiArrowLeft />
            Back to Login
          </BackButton>
          
          <LoginTitle>Forgot Password?</LoginTitle>
          <LoginSubtitle>Enter your email address and we'll send you reset instructions</LoginSubtitle>
          
          {errors.general && (
            <div style={{ 
              padding: '12px', 
              backgroundColor: '#FEE2E2', 
              color: '#DC2626', 
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '14px'
            }}>
              {errors.general}
            </div>
          )}
          
          <LoginForm onSubmit={handleSubmit}>
            <Input
              label="Email Address"
              type="email"
              name="email"
              placeholder="Enter your email address"
              value={formData.email}
              onChange={handleChange}
              error={errors.email}
              leftIcon={<FiMail />}
              required
            />
            
            <Button
              type="submit"
              variant="primary"
              size="large"
              fullWidth
              loading={loading}
            >
              Send Reset Instructions
            </Button>
          </LoginForm>
          
          <SignupPrompt>
            Remember your password? <Link to="/login">Sign in</Link>
          </SignupPrompt>
        </LoginRight>
      </LoginCard>
    </LoginContainer>
  );
};

export default ForgotPassword;
